import { useCallback, useEffect, useState } from "react";
import { api, asArray } from "@/lib/api";
import { toast } from "sonner";
import { fmtTime } from "@/lib/time";
import { BellRing, Check, Settings2, RefreshCw, ShieldAlert, X } from "lucide-react";
import { KPI, SeverityBadge, StatusBadge } from "@/components/Primitives";

// Alerts. Before this screen the product had findings — rows on a page that
// somebody had to go and look at — and no alert object at all. An alert here has
// a life: it opens on its own, a named person acknowledges it, it escalates if
// nobody does, and it resolves itself when the condition clears.

const SEVERITIES = ["critical", "high", "medium", "low", "info"];
const inputCls =
  "w-full bg-zinc-900 border border-zinc-800 rounded px-2.5 py-1.5 text-sm text-zinc-200 focus:border-zinc-600 outline-none";

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="bg-zinc-950 border border-zinc-800 rounded-lg shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-zinc-900">
          <div className="text-sm font-medium text-zinc-100">{title}</div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200"><X size={16} /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

export default function Alerts() {
  const [tab, setTab] = useState("active");
  const [alerts, setAlerts] = useState([]);
  const [rules, setRules] = useState([]);
  const [summary, setSummary] = useState(null);
  const [sev, setSev] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const state = tab === "resolved" ? "resolved" : undefined;
      const [a, s, r] = await Promise.all([
        api.get(`/alerts?limit=300${state ? `&state=${state}` : ""}${sev ? `&severity=${sev}` : ""}`),
        api.get("/alerts/summary"),
        api.get("/alerts/rules"),
      ]);
      setAlerts(asArray(a.data));
      setSummary(s.data || {});
      setRules(asArray(r.data));
      setErr("");
    } catch (e) {
      // an unreachable alert list must never look like "nothing is wrong"
      setErr(e?.response?.data?.detail || e.message || "Could not load alerts");
    } finally {
      setLoading(false);
    }
  }, [tab, sev]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const act = async (a, what) => {
    try {
      await api.post(`/alerts/${a.id}/${what}`, {});
      toast.success(what === "ack" ? "Acknowledged" : "Resolved");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || `Could not ${what}`); }
  };

  const saveRule = async () => {
    try {
      await api.patch(`/alerts/rules/${editing.id}`, {
        enabled: editing.enabled, severity: editing.severity,
        // Only send a threshold we can stand behind. `null` for an empty
        // box, and `Number("24h")` -> NaN -> null, both used to be stored
        // happily and then made `int(None)` raise inside the rule, which
        // `evaluate()` swallows -- the rule never fired again and still
        // showed as enabled. The server refuses these now; omitting the
        // key entirely means an accidental blank leaves the stored value
        // alone instead of provoking a 400 the operator did not ask for.
        ...(Number.isFinite(Number(editing.threshold)) && editing.threshold !== ""
            ? { threshold: Number(editing.threshold) } : {}),
        cooldown_minutes: Number(editing.cooldown_minutes) || 0,
        escalate_after_minutes: Number(editing.escalate_after_minutes) || 0,
        channels: editing.channels,
        webhook_url: editing.webhook_url || "",
        email_to: (editing.email_to_raw || "").split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Rule updated");
      setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
  };

  const shown = alerts;

  return (
    <div className="p-6" data-testid="alerts-page">
      <div className="flex items-center gap-3 flex-wrap">
        <BellRing size={18} className="text-zinc-400" />
        <div>
          <h1 className="text-lg font-display font-semibold text-zinc-50">Alerts</h1>
          <div className="text-xs text-zinc-500">Evaluated every five minutes; resolves itself when the condition clears</div>
        </div>
        <button onClick={load} className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded bg-zinc-900 hover:bg-zinc-800 text-zinc-300 text-sm border border-zinc-800">
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-5">
          <KPI label="Open" value={summary.open ?? 0} tone={summary.open ? "bad" : "good"} testid="al-open" />
          <KPI label="Acknowledged" value={summary.acked ?? 0} testid="al-ack" />
          <KPI label="Escalated" value={summary.escalated ?? 0} tone={summary.escalated ? "warn" : "default"} testid="al-esc" />
          <KPI label="Critical" value={summary.by_severity?.critical ?? 0} tone={summary.by_severity?.critical ? "bad" : "good"} testid="al-crit" />
          <KPI label="Resolved 24h" value={summary.resolved_24h ?? 0} tone="good" testid="al-res" />
        </div>
      )}

      <div className="flex gap-1 mt-6 border-b border-zinc-900 items-center">
        {[["active", "Active"], ["resolved", "Resolved"], ["rules", "Rules"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`alert-tab-${k}`}
            className={`px-3.5 py-2 text-[13px] border-b-2 -mb-px ${tab === k
              ? "border-blue-500 text-zinc-100" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}>{l}</button>
        ))}
        {tab !== "rules" && (
          <select value={sev} onChange={(e) => setSev(e.target.value)}
            className="ml-auto mb-1 bg-zinc-900 border border-zinc-800 rounded px-2 py-1 text-xs text-zinc-300">
            <option value="">All severities</option>
            {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        )}
      </div>

      {err && <div className="mt-4 border border-red-900/50 bg-red-950/30 text-red-300 rounded px-4 py-3 text-sm">{err}</div>}
      {loading && <div className="mt-8 text-zinc-600 text-sm">Loading…</div>}

      {!loading && !err && tab !== "rules" && (
        <div className="mt-4 bg-zinc-950 border border-zinc-900 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono border-b border-zinc-900">
                <th className="text-left px-4 py-2.5">Severity</th>
                <th className="text-left px-4 py-2.5">Alert</th>
                <th className="text-left px-4 py-2.5">Rule</th>
                <th className="text-left px-4 py-2.5">Opened</th>
                <th className="text-left px-4 py-2.5">State</th>
                <th className="text-right px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {!shown.length && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-zinc-600 text-sm">
                  {tab === "resolved" ? "Nothing resolved yet." : "Nothing is alerting. That is the good outcome."}
                </td></tr>
              )}
              {shown.map((a) => (
                <tr key={a.id} data-testid={`alert-${a.id}`} className="border-b border-zinc-900/70 hover:bg-zinc-900/40 align-top">
                  <td className="px-4 py-2.5"><SeverityBadge level={a.severity} /></td>
                  <td className="px-4 py-2.5 text-zinc-200">
                    {a.title}
                    {a.detail && <div className="text-[11.5px] text-zinc-600 mt-0.5">{a.detail}</div>}
                    {a.escalated && <div className="text-[11px] text-amber-400 mt-0.5">Escalated — nobody acknowledged in time</div>}
                  </td>
                  <td className="px-4 py-2.5 text-zinc-500 text-[12.5px]">{a.rule_name}</td>
                  <td className="px-4 py-2.5 font-mono text-[11.5px] text-zinc-500">{a.opened_at ? fmtTime(a.opened_at) : "—"}</td>
                  <td className="px-4 py-2.5">
                    <StatusBadge status={a.state === "acked" ? "in_progress" : a.state === "resolved" ? "resolved" : "open"} />
                    {a.acked_by && <div className="text-[11px] text-zinc-600 mt-0.5">by {a.acked_by}</div>}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center justify-end gap-1">
                      {a.state === "open" && (
                        <button onClick={() => act(a, "ack")} title="Acknowledge" data-testid={`ack-${a.id}`}
                          className="p-1.5 rounded hover:bg-zinc-800 text-amber-400"><Check size={14} /></button>
                      )}
                      {a.state !== "resolved" && (
                        <button onClick={() => act(a, "resolve")} title="Resolve" data-testid={`resolve-${a.id}`}
                          className="p-1.5 rounded hover:bg-zinc-800 text-emerald-400"><ShieldAlert size={14} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && !err && tab === "rules" && (
        <div className="mt-4 bg-zinc-950 border border-zinc-900 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono border-b border-zinc-900">
                <th className="text-left px-4 py-2.5">Rule</th>
                <th className="text-left px-4 py-2.5">Watches</th>
                <th className="text-left px-4 py-2.5">Threshold</th>
                <th className="text-left px-4 py-2.5">Severity</th>
                <th className="text-left px-4 py-2.5">Escalate</th>
                <th className="text-left px-4 py-2.5">Channels</th>
                <th className="text-right px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {rules.map((r) => (
                <tr key={r.id} className={`border-b border-zinc-900/70 hover:bg-zinc-900/40 ${r.enabled ? "" : "opacity-50"}`}>
                  <td className="px-4 py-2.5 text-zinc-200">{r.name}</td>
                  <td className="px-4 py-2.5 text-zinc-500 text-[12.5px]">{r.condition_label}</td>
                  <td className="px-4 py-2.5 font-mono text-[12.5px]">
                    {r.threshold == null ? "—" : `${r.threshold} ${r.threshold_unit || ""}`}
                  </td>
                  <td className="px-4 py-2.5"><SeverityBadge level={r.severity} /></td>
                  <td className="px-4 py-2.5 font-mono text-[12.5px] text-zinc-400">
                    {r.escalate_after_minutes ? `${r.escalate_after_minutes}m` : "never"}
                  </td>
                  <td className="px-4 py-2.5 text-[12.5px] text-zinc-400">{(r.channels || []).join(", ")}</td>
                  <td className="px-4 py-2.5 text-right">
                    <button onClick={() => setEditing({ ...r, email_to_raw: (r.email_to || []).join(", ") })}
                      data-testid={`rule-edit-${r.id}`}
                      className="p-1.5 rounded hover:bg-zinc-800 text-zinc-400 hover:text-zinc-100"><Settings2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <Modal title={`Edit “${editing.name}”`} onClose={() => setEditing(null)}>
          <div className="text-[13px] text-zinc-500 mb-3">{editing.condition_label}</div>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">
              Threshold {editing.threshold_unit ? `(${editing.threshold_unit})` : ""}
              <input className={`${inputCls} mt-1.5`} value={editing.threshold ?? ""}
                onChange={(e) => setEditing({ ...editing, threshold: e.target.value })} />
            </label>
            <label className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">
              Severity
              <select className={`${inputCls} mt-1.5`} value={editing.severity}
                onChange={(e) => setEditing({ ...editing, severity: e.target.value })}>
                {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">
              Re-notify after (min)
              <input className={`${inputCls} mt-1.5`} value={editing.cooldown_minutes ?? 0}
                onChange={(e) => setEditing({ ...editing, cooldown_minutes: e.target.value })} />
            </label>
            <label className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">
              Escalate after (min)
              <input className={`${inputCls} mt-1.5`} value={editing.escalate_after_minutes ?? 0}
                onChange={(e) => setEditing({ ...editing, escalate_after_minutes: e.target.value })} />
            </label>
          </div>
          <div className="mt-3">
            <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono mb-1.5">Channels</div>
            <div className="flex gap-3">
              {["inapp", "email", "webhook"].map((ch) => (
                <label key={ch} className="flex items-center gap-1.5 text-sm text-zinc-300">
                  <input type="checkbox" checked={(editing.channels || []).includes(ch)}
                    onChange={(e) => setEditing({
                      ...editing,
                      channels: e.target.checked
                        ? [...(editing.channels || []), ch]
                        : (editing.channels || []).filter((c) => c !== ch),
                    })} />
                  {ch}
                </label>
              ))}
            </div>
          </div>
          {(editing.channels || []).includes("email") && (
            <label className="block mt-3 text-[11px] uppercase tracking-widest text-zinc-500 font-mono">
              Email recipients (comma separated)
              <input className={`${inputCls} mt-1.5`} value={editing.email_to_raw || ""}
                onChange={(e) => setEditing({ ...editing, email_to_raw: e.target.value })} />
            </label>
          )}
          {(editing.channels || []).includes("webhook") && (
            <label className="block mt-3 text-[11px] uppercase tracking-widest text-zinc-500 font-mono">
              Webhook URL (Teams or Slack incoming webhook)
              <input className={`${inputCls} mt-1.5`} value={editing.webhook_url || ""}
                onChange={(e) => setEditing({ ...editing, webhook_url: e.target.value })} />
            </label>
          )}
          <label className="flex items-center gap-2 mt-4 text-sm text-zinc-300">
            <input type="checkbox" checked={!!editing.enabled}
              onChange={(e) => setEditing({ ...editing, enabled: e.target.checked })} />
            Rule enabled
          </label>
          <button onClick={saveRule} data-testid="rule-save"
            className="mt-4 w-full py-2 rounded bg-blue-600 hover:bg-blue-500 text-white text-sm">
            Save rule
          </button>
        </Modal>
      )}
    </div>
  );
}
